// Análises com IA: a lista (com o consumo do mês e o pedido de pergunta ou relatório) e o
// documento — o laudo, o relatório ou a conversa, acompanhando o andamento enquanto roda.
//
// O laudo pós-disparo nasce na tela da oscilografia; aqui se pede a pergunta e o relatório.

import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Bot, FileText, Loader2, MessageSquare, Send, Sparkles, Trash2, Waves } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Markdown } from "../components/Markdown";
import { Alert, Badge, Button, Card, EmptyState, Field, Modal, PageHeader, Select, Spinner, Table, cx } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { useAction, useTree } from "../lib/hooks";

// ── Tipos ────────────────────────────────────────────────────────────────────

type Kind = "post_trip" | "periodic" | "chat";
type Status = "running" | "done" | "error";

export interface AnalysisQuota {
  enabled: boolean;
  model: string | null;
  groups: { groupId: number; groupName: string; used: number; limit: number }[];
}

interface AnalysisRow {
  id: number;
  kind: Kind;
  title: string;
  site_name: string;
  regulator_name: string | null;
  file_id: number | null;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  status: Status | null;
  turns: number | null;
}

interface Step {
  tool: string;
  input: unknown;
  ok: boolean;
  ms: number;
}

interface Turn {
  id: number;
  seq: number;
  prompt: string;
  status: Status;
  answer: string | null;
  error: string | null;
  steps: Step[];
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  created_at: string;
  finished_at: string | null;
  user_name: string | null;
}

interface AnalysisDetail {
  id: number;
  kind: Kind;
  title: string;
  site_id: number;
  site_name: string;
  regulator_id: number | null;
  regulator_name: string | null;
  file_id: number | null;
  file_name: string | null;
  period_from: string | null;
  period_to: string | null;
  created_at: string;
  created_by_name: string | null;
  turns: Turn[];
  canContinue: boolean;
  canDelete: boolean;
}

const KIND: Record<Kind, { label: string; icon: typeof Bot }> = {
  post_trip: { label: "Laudo pós-disparo", icon: Waves },
  periodic: { label: "Relatório", icon: FileText },
  chat: { label: "Conversa", icon: MessageSquare },
};

// Nome de cada consulta do modelo, como o usuário entende.
const TOOL_LABEL: Record<string, string> = {
  list_regulators: "reguladores",
  get_live_state: "estado atual",
  list_signals: "grandezas disponíveis",
  get_trend: "tendência",
  get_alarm_history: "histórico de alarmes",
  get_digital_changes: "estados digitais",
  get_soe: "sequência de eventos",
  list_oscillographies: "oscilografias",
  get_oscillography: "oscilografia",
  get_connectivity: "comunicação",
};

export const useAnalysisQuota = () =>
  useQuery({ queryKey: ["analysis-quota"], queryFn: () => api.get<AnalysisQuota>("/api/analysis/quota"), staleTime: 60_000 });

const fmt = (iso: string) => new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

const textarea =
  "w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-fg placeholder:text-muted " +
  "focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand disabled:opacity-60";

function StatusBadge({ status }: { status: Status | null }) {
  if (status === "running") return <Badge tone="brand">em andamento</Badge>;
  if (status === "error") return <Badge tone="danger">falhou</Badge>;
  return <Badge tone="ok">concluída</Badge>;
}

// ── Lista ────────────────────────────────────────────────────────────────────

export function AnalysisListPage() {
  const quota = useAnalysisQuota();
  const list = useQuery({
    queryKey: ["analyses"],
    queryFn: () => api.get<{ analyses: AnalysisRow[] }>("/api/analysis"),
    // Enquanto algo roda, a situação na lista acompanha.
    refetchInterval: (q) => (q.state.data?.analyses.some((a) => a.status === "running") ? 3000 : false),
  });

  return (
    <>
      <PageHeader
        title="Análises com IA"
        description="Laudos de disparo, relatórios de período e perguntas respondidas pelo Claude a partir dos dados da plataforma."
      />

      {quota.data && !quota.data.enabled && (
        <div className="mb-4">
          <Alert>As análises com IA não estão configuradas nesta instalação.</Alert>
        </div>
      )}

      {quota.data?.enabled && (
        <div className="mb-6 grid gap-6 lg:grid-cols-[1fr_18rem]">
          <NewAnalysis />
          <QuotaCard quota={quota.data} />
        </div>
      )}

      {list.isLoading ? (
        <Spinner />
      ) : !list.data?.analyses.length ? (
        <EmptyState title="Nenhuma análise ainda">
          Faça uma pergunta acima, gere um relatório do período, ou abra uma oscilografia e peça o laudo do disparo.
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Análise</th>
              <th>Onde</th>
              <th>Situação</th>
              <th>Atualizada</th>
            </tr>
          </thead>
          <tbody>
            {list.data.analyses.map((a) => {
              const K = KIND[a.kind];
              return (
                <tr key={a.id} className="hover:bg-surface-2">
                  <td>
                    <Link to={`/analises/${a.id}`} className="flex items-start gap-2 font-medium hover:underline">
                      <K.icon className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
                      <span>{a.title}</span>
                    </Link>
                    <p className="ml-6 text-xs text-muted">
                      {K.label}
                      {a.kind === "chat" && a.turns && a.turns > 1 ? ` · ${a.turns} perguntas` : ""}
                      {a.created_by_name ? ` · ${a.created_by_name}` : ""}
                    </p>
                  </td>
                  <td className="text-sm text-muted">
                    {a.site_name}
                    {a.regulator_name ? ` › ${a.regulator_name}` : " (usina inteira)"}
                  </td>
                  <td>
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="whitespace-nowrap text-sm text-muted">{fmt(a.updated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
}

function QuotaCard({ quota }: { quota: AnalysisQuota }) {
  return (
    <Card className="p-4">
      <h2 className="mb-3 text-sm font-semibold">Consumo deste mês</h2>
      <div className="space-y-3">
        {quota.groups.map((g) => {
          const pct = g.limit ? Math.min(100, (g.used / g.limit) * 100) : 100;
          return (
            <div key={g.groupId}>
              <div className="mb-1 flex justify-between text-xs">
                <span className="truncate">{g.groupName}</span>
                <span className="text-muted tabular-nums">{g.limit ? `${g.used} de ${g.limit}` : "desligado"}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
                <div className={cx("h-full rounded-full", pct >= 100 ? "bg-danger" : pct >= 80 ? "bg-warn" : "bg-brand")} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-muted">Cada laudo, relatório ou pergunta conta uma análise. O contador zera no dia 1º.</p>
    </Card>
  );
}

/** Alvos possíveis: cada usina inteira e cada regulador. Valor "s:<id>" ou "r:<id>". */
function useTargets() {
  const { data: tree } = useTree();
  return useMemo(
    () =>
      tree?.groups.flatMap((g) =>
        g.sites.map((s) => ({
          site: { value: `s:${s.id}`, label: `${s.name} — usina inteira` },
          regulators: s.units.flatMap((u) => u.regulators.map((r) => ({ value: `r:${r.id}`, label: `${s.name} › ${u.name} › ${r.name}` }))),
        })),
      ) ?? [],
    [tree],
  );
}

function targetBody(target: string) {
  const [k, v] = target.split(":");
  return k === "r" ? { regulatorId: Number(v) } : { siteId: Number(v) };
}

const PERIODS = [
  { value: "7", label: "Últimos 7 dias" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "prev-month", label: "Mês anterior" },
];

function periodRange(p: string): { from: Date; to: Date } {
  const now = new Date();
  if (p === "prev-month") {
    return { from: new Date(now.getFullYear(), now.getMonth() - 1, 1), to: new Date(now.getFullYear(), now.getMonth(), 1) };
  }
  return { from: new Date(now.getTime() - Number(p) * 86_400_000), to: now };
}

function NewAnalysis() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const targets = useTargets();
  const [mode, setMode] = useState<"chat" | "periodic">(params.get("nova") === "relatorio" ? "periodic" : "chat");
  const [target, setTarget] = useState(params.get("reg") ? `r:${params.get("reg")}` : "");
  const [question, setQuestion] = useState("");
  const [period, setPeriod] = useState("7");
  const { run, busy, error } = useAction([["analyses"], ["analysis-quota"]]);

  // Sem alvo escolhido: o primeiro regulador visível (o caso mais comum é ter um só).
  useEffect(() => {
    if (!target && targets.length) setTarget(targets[0]!.regulators[0]?.value ?? targets[0]!.site.value);
  }, [target, targets]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    let id: number | null = null;
    await run(async () => {
      const r =
        mode === "chat"
          ? await api.post<{ id: number }>("/api/analysis/chat", { ...targetBody(target), question })
          : await api.post<{ id: number }>("/api/analysis/periodic", { ...targetBody(target), ...periodRange(period) });
      id = r.id;
    });
    if (id !== null) navigate(`/analises/${id}`);
  }

  return (
    <Card className="p-4">
      <div className="mb-4 flex gap-1 rounded-md bg-surface-2 p-1 text-sm" role="tablist">
        {(
          [
            ["chat", "Perguntar", MessageSquare],
            ["periodic", "Relatório do período", FileText],
          ] as const
        ).map(([m, label, Icon]) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            className={cx("flex flex-1 items-center justify-center gap-2 rounded px-3 py-1.5", mode === m ? "bg-surface font-medium shadow-sm" : "text-muted hover:text-fg")}
          >
            <Icon className="size-4" aria-hidden /> {label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Sobre">
          {(id) => (
            <Select id={id} value={target} onChange={(e) => setTarget(e.target.value)} required>
              {targets.map((s) => (
                <optgroup key={s.site.value} label={s.site.label.replace(" — usina inteira", "")}>
                  {s.regulators.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                  <option value={s.site.value}>{s.site.label}</option>
                </optgroup>
              ))}
            </Select>
          )}
        </Field>

        {mode === "chat" ? (
          <Field label="Pergunta" hint="Ex.: por que a tensão oscilou ontem à tarde? Quantos disparos houve este mês e por quê?">
            {(id) => (
              <textarea
                id={id}
                className={textarea}
                rows={3}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                maxLength={4000}
                required
                minLength={2}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) e.currentTarget.form?.requestSubmit();
                }}
              />
            )}
          </Field>
        ) : (
          <Field label="Período">
            {(id) => (
              <Select id={id} value={period} onChange={(e) => setPeriod(e.target.value)}>
                {PERIODS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted">
            {mode === "chat" ? "Ctrl+Enter envia." : "Leva alguns minutos: o modelo consulta alarmes, disparos e tendências."} Para o laudo de um disparo, abra a
            oscilografia.
          </p>
          <Button type="submit" loading={busy} disabled={!target}>
            <Sparkles className="size-4" aria-hidden />
            {mode === "chat" ? "Perguntar" : "Gerar relatório"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ── Documento ────────────────────────────────────────────────────────────────

export function AnalysisPage() {
  const analysisId = Number(useParams().id);
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useAction([["analyses"]]);
  const q = useQuery({
    queryKey: ["analysis", analysisId],
    queryFn: () => api.get<AnalysisDetail>(`/api/analysis/${analysisId}`),
    // Enquanto o modelo trabalha, a tela acompanha o andamento (consultas e texto parcial).
    refetchInterval: (query) => (query.state.data?.turns.some((t) => t.status === "running") ? 1500 : false),
  });

  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <EmptyState title="Análise não encontrada" />;
  const a = q.data;
  const running = a.turns.some((t) => t.status === "running");
  const K = KIND[a.kind];

  return (
    <>
      <Link to="/analises" className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-fg">
        <ArrowLeft className="size-4" aria-hidden /> Análises
      </Link>
      <PageHeader
        title={a.title}
        description={`${K.label} · ${a.site_name}${a.regulator_name ? ` › ${a.regulator_name}` : " (usina inteira)"}`}
        actions={
          <div className="flex gap-2">
            {a.file_id && (
              <Link
                to={`/oscilografias/${a.file_id}`}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-line bg-surface px-4 text-sm font-medium hover:bg-surface-2"
              >
                <Waves className="size-4" aria-hidden /> Oscilografia
              </Link>
            )}
            {a.canDelete && (
              <Button variant="secondary" onClick={() => setConfirmDelete(true)} disabled={running} title={running ? "Aguarde terminar" : undefined}>
                <Trash2 className="size-4" aria-hidden /> Apagar
              </Button>
            )}
          </div>
        }
      />
      <p className="mb-6 text-xs text-muted">
        Pedida por {a.created_by_name ?? "—"} em {fmt(a.created_at)}. Texto gerado por IA a partir dos dados da plataforma: confira os valores citados antes de
        tomar decisões.
      </p>

      <div className="space-y-6">
        {a.turns.map((t) => (
          <TurnView key={t.id} turn={t} analysis={a} showPrompt={a.kind === "chat"} onRetried={() => q.refetch()} />
        ))}
      </div>

      {a.canContinue && <Composer analysisId={a.id} disabled={running} onSent={() => q.refetch()} />}

      {confirmDelete && (
        <Modal
          open
          onClose={() => setConfirmDelete(false)}
          title="Apagar análise"
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                loading={del.busy}
                onClick={async () => {
                  if (await del.run(() => api.del(`/api/analysis/${a.id}`))) navigate("/analises");
                }}
              >
                Apagar
              </Button>
            </>
          }
        >
          {del.error && <Alert kind="error">{del.error}</Alert>}
          <p className="text-sm">Apagar “{a.title}”? O consumo do mês não é devolvido.</p>
        </Modal>
      )}
    </>
  );
}

function TurnView({ turn: t, analysis: a, showPrompt, onRetried }: { turn: Turn; analysis: AnalysisDetail; showPrompt: boolean; onRetried: () => void }) {
  const retry = useAction([["analyses"], ["analysis-quota"]]);
  const navigate = useNavigate();
  const consulted = [...new Set(t.steps.map((s) => TOOL_LABEL[s.tool] ?? s.tool))];

  async function again() {
    let id: number | null = null;
    await retry.run(async () => {
      const r =
        a.kind === "post_trip"
          ? await api.post<{ id: number }>("/api/analysis/post-trip", { fileId: a.file_id, again: true })
          : await api.post<{ id: number }>("/api/analysis/periodic", {
              ...(a.regulator_id ? { regulatorId: a.regulator_id } : { siteId: a.site_id }),
              from: a.period_from,
              to: a.period_to,
            });
      id = r.id;
    });
    if (id !== null) {
      if (id === a.id) onRetried();
      else navigate(`/analises/${id}`);
    }
  }

  return (
    <div className="space-y-3">
      {showPrompt && (
        <div className="ml-auto max-w-[85%] rounded-lg bg-brand/10 px-4 py-2.5 text-sm whitespace-pre-wrap sm:max-w-[70%]">
          {t.prompt}
          <p className="mt-1 text-right text-xs text-muted">
            {t.user_name ?? ""} · {fmt(t.created_at)}
          </p>
        </div>
      )}

      <Card className="p-5">
        {t.status === "running" && (
          <div className="mb-3 flex items-start gap-3 text-sm text-muted" role="status">
            <Loader2 className="mt-0.5 size-5 shrink-0 animate-spin text-brand" aria-hidden />
            <div>
              <p className="font-medium text-fg">Analisando…</p>
              <p>{consulted.length ? `Consultou: ${consulted.join(", ")}.` : "Preparando as consultas."}</p>
            </div>
          </div>
        )}

        {t.status === "error" && (
          <div className="space-y-3">
            <Alert kind="error">A análise falhou: {t.error ?? "erro desconhecido"}.</Alert>
            {a.kind !== "chat" && (
              <>
                {retry.error && <Alert kind="error">{retry.error}</Alert>}
                <Button variant="secondary" size="sm" loading={retry.busy} onClick={again}>
                  Tentar de novo
                </Button>
              </>
            )}
          </div>
        )}

        {t.answer && <Markdown text={t.answer} />}

        {t.status === "done" && (
          <details className="mt-4 border-t border-line pt-3 text-xs text-muted">
            <summary className="cursor-pointer select-none">
              {t.steps.length} {t.steps.length === 1 ? "consulta" : "consultas"} aos dados
              {t.finished_at ? ` · ${Math.max(1, Math.round((Date.parse(t.finished_at) - Date.parse(t.created_at)) / 1000))} s` : ""}
            </summary>
            <ol className="mt-2 space-y-1">
              {t.steps.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className={cx("shrink-0", s.ok ? "" : "text-danger")}>{TOOL_LABEL[s.tool] ?? s.tool}</span>
                  <code className="truncate font-mono">{JSON.stringify(s.input)}</code>
                  {!s.ok && <span className="text-danger">(recusada)</span>}
                </li>
              ))}
            </ol>
            <p className="mt-2">
              {t.model} · {(t.input_tokens + t.cache_read_tokens).toLocaleString("pt-BR")} tokens lidos ({t.cache_read_tokens.toLocaleString("pt-BR")} em cache) ·{" "}
              {t.output_tokens.toLocaleString("pt-BR")} escritos
            </p>
          </details>
        )}
      </Card>
    </div>
  );
}

function Composer({ analysisId, disabled, onSent }: { analysisId: number; disabled: boolean; onSent: () => void }) {
  const [question, setQuestion] = useState("");
  const { run, busy, error } = useAction([["analyses"], ["analysis-quota"]]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const ok = await run(() => api.post(`/api/analysis/${analysisId}/messages`, { question }));
    if (ok) {
      setQuestion("");
      onSent();
    }
  }

  return (
    <form onSubmit={submit} className="mt-6 space-y-2">
      {error && <Alert kind="error">{error}</Alert>}
      <div className="flex items-end gap-2">
        <textarea
          aria-label="Nova pergunta"
          className={textarea}
          rows={2}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={disabled ? "Aguarde a resposta…" : "Continue a conversa (Ctrl+Enter envia)"}
          maxLength={4000}
          minLength={2}
          required
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) e.currentTarget.form?.requestSubmit();
          }}
        />
        <Button type="submit" loading={busy} disabled={disabled} aria-label="Enviar">
          <Send className="size-4" aria-hidden />
        </Button>
      </div>
    </form>
  );
}

/** Botão da tela da oscilografia: abre o laudo existente ou pede um novo. */
export function PostTripButton({ fileId }: { fileId: number }) {
  const quota = useAnalysisQuota();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const existing = useQuery({
    queryKey: ["analyses", "file", fileId],
    queryFn: () => api.get<{ analyses: AnalysisRow[] }>(`/api/analysis?kind=post_trip&fileId=${fileId}`),
    enabled: !!quota.data?.enabled,
  });
  if (!quota.data?.enabled) return null;
  const found = existing.data?.analyses.find((x) => x.status !== "error");

  async function go() {
    if (found) return navigate(`/analises/${found.id}`);
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ id: number }>("/api/analysis/post-trip", { fileId });
      navigate(`/analises/${r.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "não foi possível pedir o laudo");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button onClick={go} loading={busy}>
        <Sparkles className="size-4" aria-hidden /> {found ? "Ver laudo da IA" : "Laudo com IA"}
      </Button>
      {error && <p className="max-w-64 text-right text-xs text-danger">{error}</p>}
    </div>
  );
}
